// «Tablero del Presidente» — vista ejecutiva entre dependencias (Orion for Government).
// Móvil primero: el presidente lo abre en el teléfono. Cada mosaico dice si sus
// cifras son reales (Güicho) o de Demostración, o si la dependencia aún no está
// conectada. Los totales vienen del servidor (gobierno_resumen_ejecutivo).

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2, Dog, HeartPulse, Landmark, MapPinned, MessageCircle, Radar, ShieldAlert, X,
} from 'lucide-react';

import { ChatPane } from '@/components/assistant/ChatPane';
import { OsApp, OsTabs } from '@/modules/orion-shell/OsApp';
import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { GovMap, type MapPoint, OrigenBadge, fmt, govFetch } from '@/modules/gov-health';

type Periodo = 'semana' | 'mes';

interface Tile {
  clave: string; titulo: string; dependencia: string; estado: string; origen: string; mensaje?: string; nota?: string;
  kpis: Record<string, any>; puntos: any[];
  top_categorias?: { etiqueta: string; n: number }[];
  pendientes_por_dependencia?: { dependencia: string; abiertos: number }[];
  top_senal?: { etiqueta: string; colonia: string; nivel: string; explicacion: string } | null;
  proximas?: { fecha: string; colonia: string }[];
  // salud
  jornadas?: number; personas_atendidas?: number; alertas?: number; vigilancia?: number; canalizaciones?: number;
  // bienestar animal (apps/gov_animal)
  proxima_jornada_sugerida?: { colonia: string; fecha_sugerida: string } | null;
}
interface Resumen { periodo: { desde: string; hasta: string }; tiles: Tile[]; mapa: { capa: string; lat: number; lon: number; etiqueta: string; nivel: string; n?: number | null }[]; destacados: string[]; nota: string }

const ICON: Record<string, any> = {
  atencion_ciudadana: Building2, seguridad: ShieldAlert, bienestar_animal: Dog, salud: HeartPulse, desarrollo_urbano: Landmark,
};
const CAPA: Record<string, { label: string; color: string }> = {
  atencion: { label: 'Atención ciudadana', color: '#7C3AED' },
  salud: { label: 'Salud', color: '#EF4444' },
  animal: { label: 'Bienestar animal', color: '#10B981' },
  seguridad: { label: 'Seguridad', color: '#F59E0B' },
};

export function PresidentBoard() {
  const [periodo, setPeriodo] = useState<Periodo>('semana');
  const [chat, setChat] = useState(false);
  const [capas, setCapas] = useState<Record<string, boolean>>({ atencion: true, salud: true, animal: true, seguridad: true });
  const q = useQuery({
    queryKey: ['gov-executive', periodo],
    queryFn: () => govFetch<Resumen>(`/api/v1/admin/gov-executive/resumen?periodo=${periodo}`),
    retry: false,
  });
  const navigate = useNavigate();
  const intel = useQuery({
    queryKey: ['gov-executive', 'inteligencia', '30'],
    queryFn: () => govFetch<any>('/api/v1/admin/gov-executive/inteligencia?dias=30'),
    retry: false, staleTime: 5 * 60_000,
  });
  const top = intel.data?.estado === 'ok' ? intel.data.prioridades?.[0] : null;
  const points: MapPoint[] = useMemo(() => (q.data?.mapa ?? []).filter((p) => capas[p.capa]).map((p) => ({
    lat: p.lat, lon: p.lon, label: p.etiqueta || CAPA[p.capa]?.label || '', color: CAPA[p.capa]?.color ?? '#8B5CF6',
    radius: p.capa === 'salud' ? 12 : Math.min(18, 5 + Math.sqrt(p.n ?? 1) * 1.6),
    detail: `${CAPA[p.capa]?.label ?? ''}${p.n ? ` · ${p.n} ${p.capa === 'seguridad' ? 'alerta(s) confirmada(s)' : 'reporte(s)'}` : ''}${p.nivel ? ` · ${p.nivel}` : ''}`,
  })), [q.data, capas]);

  const rango = q.data ? `${fmtFecha(q.data.periodo.desde)} – ${fmtFecha(q.data.periodo.hasta)}` : '';
  return (
    <OsApp
      icon={Landmark}
      title="Tablero del Presidente"
      subtitle={rango || 'Municipio de San Luis Potosí'}
      tabs={<OsTabs label="Periodo" value={periodo} onChange={setPeriodo} items={[{ key: 'semana', label: 'Esta semana' }, { key: 'mes', label: '30 días' }]} />}
      actions={
        <button type="button" onClick={() => setChat(true)}
          className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold">
          <MessageCircle size={15} /> <span className="sm:hidden">Orión</span><span className="hidden sm:inline">Pregúntale a Orión</span>
        </button>
      }
    >
      {q.isLoading ? <Loading /> : q.isError ? (
        (q.error as any)?.status === 404 ? (
          <EmptyState icon={<Landmark size={18} />} title="El Tablero del Presidente no está activo para esta organización" />
        ) : (q.error as any)?.status === 403 ? (
          <EmptyState icon={<Landmark size={18} />} title="Este tablero es para la Presidencia">Pide el rol «presidente» a la administración.</EmptyState>
        ) : <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : (
        <div className="flex flex-col gap-4">
          {top && (
            <button type="button" onClick={() => navigate('/m/gov-executive/inteligencia')}
              className="flex w-full flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left hover:bg-muted/60">
              <Radar size={18} className="shrink-0 text-primary" />
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary">Inteligencia de medios · lo que más pesa hoy</span>
                <span className="block truncate text-sm font-semibold">{top.titulo}</span>
                <span className="block text-xs text-muted-foreground">{intel.data.total} notas de {intel.data.medios} medios en 30 días · {top.dependencia}</span>
              </span>
              <span className="text-xs font-medium text-primary">Ver briefing →</span>
            </button>
          )}
          {q.data!.destacados.length > 0 && (
            <section aria-label="Lo más importante" className="rounded-2xl border border-primary/25 bg-primary/5 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-primary">Lo más importante</h2>
              <ul className="mt-2 space-y-1.5 text-[15px] leading-snug text-foreground">
                {q.data!.destacados.map((d) => <li key={d} className="flex gap-2"><span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{d}</li>)}
              </ul>
            </section>
          )}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {q.data!.tiles.map((t) => <TileCard key={t.clave} t={t} />)}
          </div>
          <Card title={<span className="inline-flex items-center gap-1.5"><MapPinned size={16} className="text-primary" /> Mapa del municipio</span>}>
            <div className="mb-2 flex flex-wrap gap-2" role="group" aria-label="Capas del mapa">
              {Object.entries(CAPA).map(([k, v]) => (
                <button key={k} type="button" aria-pressed={capas[k]} onClick={() => setCapas((c) => ({ ...c, [k]: !c[k] }))}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-medium ${capas[k] ? 'border-border bg-card text-foreground' : 'border-dashed border-border text-muted-foreground'}`}>
                  <i className="h-2.5 w-2.5 rounded-full" style={{ background: v.color, opacity: capas[k] ? 1 : 0.35 }} /> {v.label}
                </button>
              ))}
            </div>
            <GovMap points={points} height={340} zoom={12} label="Mapa con reportes ciudadanos, señales de salud y bienestar animal" />
          </Card>
          <p className="text-[11px] text-muted-foreground">{q.data!.nota}</p>
        </div>
      )}
      {chat && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center" role="dialog" aria-modal="true" aria-label="Pregúntale a Orión">
          <div className="flex h-[85dvh] w-full flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl sm:h-[78vh] sm:max-w-xl sm:rounded-3xl">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <MessageCircle size={16} className="text-primary" />
              <p className="flex-1 text-sm font-semibold">Pregúntale a Orión · {periodo === 'semana' ? 'esta semana' : '30 días'}</p>
              <button type="button" onClick={() => setChat(false)} aria-label="Cerrar" className="grid h-9 w-9 place-items-center rounded-xl hover:bg-muted"><X size={16} /></button>
            </div>
            <div className="min-h-0 flex-1">
              <ChatPane
                startFresh
                hideThinking
                focusTools="gov-executive"
                messagePrefix={`[Tablero del Presidente · periodo: ${periodo}] Usa el resumen ejecutivo del municipio; di siempre si una cifra es de demostración o si la dependencia aún no está conectada.`}
                emptyTitle="¿Qué quieres saber del municipio?"
                emptySubtitle="Orión responde con los datos del tablero, por dependencia."
                suggestions={['¿Qué es lo más urgente esta semana?', 'Resume la semana por dependencia', '¿Cómo va la Ruta de la Salud?', '¿Qué reportes ciudadanos siguen abiertos?']}
                webChatHeaders={(() => { const o = localStorage.getItem('org_id'); return o ? { 'x-org-id': o } : {}; })()}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </OsApp>
  );
}

function TileCard({ t }: { t: Tile }) {
  const Icon = ICON[t.clave] ?? Building2;
  const k = t.kpis ?? {};
  const vacio = t.estado === 'no_disponible' || t.estado === 'proximamente' || t.estado === 'error';
  return (
    <article className="orion-card flex min-w-0 flex-col rounded-[20px] border border-border bg-card p-4">
      <header className="flex items-start gap-3">
        <span className="os-glyph grid h-10 w-10 shrink-0 place-items-center rounded-xl" aria-hidden><Icon size={19} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-[15px] font-semibold leading-tight">{t.titulo}</h3>
            <OrigenBadge origen={t.origen} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{t.dependencia}</p>
        </div>
      </header>
      {vacio ? (
        <p className="mt-4 rounded-xl border border-dashed border-border p-3 text-[13px] text-muted-foreground">{t.mensaje}</p>
      ) : t.clave === 'salud' ? (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Kpi label="Atendidos" v={t.personas_atendidas} />
            <Kpi label="Jornadas" v={t.jornadas} />
            <Kpi label="Alertas" v={t.alertas} tone={t.alertas ? 'red' : undefined} />
          </div>
          {t.top_senal && <p className="mt-3 text-[13px] text-muted-foreground"><strong className="text-foreground">{t.top_senal.etiqueta} · {t.top_senal.colonia}.</strong> Para revisión de Salud; no es un diagnóstico.</p>}
          {t.proximas?.[0] && <p className="mt-2 text-xs text-muted-foreground">Próxima jornada: {fmtFecha(t.proximas[0].fecha)} en {t.proximas[0].colonia}</p>}
        </>
      ) : t.clave === 'seguridad' ? (
        // apps/gov_security.resumen_ejecutivo: solo alertas que un operador confirmó.
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Kpi label="Confirmadas" v={k.confirmadas_por_operador} />
            <Kpi label="Despachadas" v={k.despachadas} />
            <Kpi label="Por revisar" v={k.pendientes_de_revision} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {k.pct_descartadas != null ? `${k.pct_descartadas}% descartadas por un operador` : 'Sin decisiones en el periodo'}
            {k.min_a_confirmacion != null ? ` · ${k.min_a_confirmacion} min a confirmación` : ''}
          </p>
          {t.nota && <p className="mt-2 text-[11px] text-muted-foreground">{t.nota}</p>}
        </>
      ) : t.clave === 'bienestar_animal' && k.reportes !== undefined ? (
        // apps/gov_animal.resumen_ejecutivo (CIBA conectado).
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Kpi label="Reportes" v={k.reportes} sub={k.desde_camaras ? `${k.desde_camaras} de cámaras` : undefined} />
            <Kpi label="Esterilizaciones" v={k.esterilizaciones} />
            <Kpi label="Abiertos" v={k.reportes_abiertos} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {fmt(k.vacunas_antirrabicas ?? null)} vacunas antirrábicas · {fmt(k.adopciones_entregadas ?? null)} adopciones entregadas
          </p>
          {t.proxima_jornada_sugerida && (
            <p className="mt-2 text-xs text-muted-foreground">Próxima jornada sugerida: {t.proxima_jornada_sugerida.colonia} ({fmtFecha(t.proxima_jornada_sugerida.fecha_sugerida)})</p>
          )}
        </>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Kpi label="Recibidos" v={k.recibidos} />
            <Kpi label="Atendidos" v={k.atendidos} sub={k.pct_atendido != null ? `${k.pct_atendido}%` : undefined} />
            <Kpi label={t.clave === 'atencion_ciudadana' ? 'Urgentes' : 'Abiertos'} v={t.clave === 'atencion_ciudadana' ? k.urgentes_abiertos : k.abiertos_hoy}
              tone={(t.clave === 'atencion_ciudadana' ? k.urgentes_abiertos : 0) ? 'red' : undefined} />
          </div>
          {t.top_categorias && t.top_categorias.length > 0 && (
            <p className="mt-3 truncate text-xs text-muted-foreground">Más reportado: {t.top_categorias.slice(0, 3).map((c) => `${c.etiqueta} (${c.n})`).join(' · ')}</p>
          )}
          {t.nota && <p className="mt-2 text-[11px] text-muted-foreground">{t.nota}</p>}
        </>
      )}
    </article>
  );
}

function Kpi({ label, v, sub, tone }: { label: string; v: number | null | undefined; sub?: string; tone?: 'red' }) {
  return (
    <div className="min-w-0 rounded-xl bg-muted/50 px-2.5 py-2">
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold leading-tight tabular-nums ${tone === 'red' ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmt(v ?? null)}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function fmtFecha(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}
